import React, { ChangeEventHandler, forwardRef, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { ReservationFlowQuestionnaireFieldItem } from 'src/interfaces/reservation_flow';
import { useDataAttributeProps } from 'src/hooks/useDataAttributeProps';
import { RSCheckBoxWithLabel } from 'src/reservation-page/RSCheckBoxWithLabel';

export interface QuestionnaireInputCheckBoxProps {
  [key: `data-${string}`]: string | undefined;
  value?: string[];
  onChange: (value: string[]) => void;
  onBlur: () => void;
  item: ReservationFlowQuestionnaireFieldItem;
}

export const QuestionnaireInputCheckBox = forwardRef<
  HTMLDivElement,
  QuestionnaireInputCheckBoxProps
>((props, ref) => {
  const dataAttributes = useDataAttributeProps(props);
  const { value, onChange, onBlur, item } = props;

  const intl = useIntl();

  const onInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const changedValue = event.target.value;

      if (!value) {
        onChange([changedValue]);
      } else if (value.includes(changedValue)) {
        const newValue = value.filter((v) => v !== changedValue);

        if (newValue.length > 0) {
          onChange(newValue);
        } else {
          onChange([]);
        }
      } else {
        onChange([...value, changedValue]);
      }

      onBlur();
    },
    [value, onChange, onBlur],
  );

  return (
    <div ref={ref}>
      <div className="mb-1.5">
        <div className="rs-font text-16 text-gray-1000 pc:text-15 whitespace-pre-wrap font-normal">
          {item.label}
          {item.is_required && (
            <span className="text-red-700">
              {intl.formatMessage({
                defaultMessage: '（必須）',
                id: 'KJh+nV',
                description:
                  'src/templates/merchants/resources/book/components/questionnaire/QuestionnaireInputCheckBox/index.tsx',
              })}
            </span>
          )}
        </div>
      </div>
      <div>
        {item.options &&
          item.options.map((option) => {
            return (
              <div
                key={`questionnaire_${item.id}_${option}`}
                className="min-h-44 flex items-center"
              >
                <RSCheckBoxWithLabel
                  {...dataAttributes}
                  labelText={option}
                  value={option}
                  name={`questionnaire_${item.id}`}
                  checked={
                    typeof value !== 'undefined'
                      ? value.includes(option)
                      : false
                  }
                  onChange={onInputChange}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
});

QuestionnaireInputCheckBox.displayName = 'QuestionnaireInputCheckBox';
